import { useQuery } from "@tanstack/react-query";
import {
  fetchDeviceLimitExceptionList,
  fetchAllSubscriptions,
  fetchAllInvites,
  fetchNYCampaignUsers,
  fetchCollectionCounts,
  fetchFreeTrials,
  fetchAllUsers,
  fetchStaffStudents,
} from "./fetchFunctions";

/**
 * React Query hook for device limit exception list
 */
export const useDeviceLimitExceptionList = () => {
  return useQuery({
    queryKey: ["summary", "deviceLimitExceptionList"],
    queryFn: fetchDeviceLimitExceptionList,
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * React Query hook for all subscriptions
 * Depends on deviceLimitExceptionList
 */
export const useAllSubscriptions = (deviceLimitExceptionList = []) => {
  // Use only the length for stable query key (content doesn't affect filtering logic significantly)
  // This prevents infinite loops from array reference changes
  const listKey = Array.isArray(deviceLimitExceptionList)
    ? deviceLimitExceptionList.length
    : 0;
  return useQuery({
    queryKey: ["summary", "subscriptions", listKey],
    queryFn: () => fetchAllSubscriptions(deviceLimitExceptionList),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 2, // Retry up to 2 times on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    enabled:
      deviceLimitExceptionList !== null &&
      deviceLimitExceptionList !== undefined,
  });
};

/**
 * React Query hook for all invites
 * Depends on deviceLimitExceptionList
 */
export const useAllInvites = (deviceLimitExceptionList = []) => {
  // Use only the length for stable query key
  const listKey = Array.isArray(deviceLimitExceptionList)
    ? deviceLimitExceptionList.length
    : 0;
  return useQuery({
    queryKey: ["summary", "invites", listKey],
    queryFn: () => fetchAllInvites(deviceLimitExceptionList),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    enabled:
      deviceLimitExceptionList !== null &&
      deviceLimitExceptionList !== undefined,
  });
};

/**
 * React Query hook for NY Campaign users
 */
export const useNYCampaignUsers = (deviceLimitExceptionList = []) => {
  // Use only the length for stable query key
  const listKey = Array.isArray(deviceLimitExceptionList)
    ? deviceLimitExceptionList.length
    : 0;
  return useQuery({
    queryKey: ["summary", "nyCampaignUsers", listKey],
    queryFn: () => fetchNYCampaignUsers(deviceLimitExceptionList),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    enabled:
      deviceLimitExceptionList !== null &&
      deviceLimitExceptionList !== undefined,
  });
};

/**
 * React Query hook for collection counts
 */
export const useCollectionCounts = () => {
  return useQuery({
    queryKey: ["summary", "collectionCounts"],
    queryFn: fetchCollectionCounts,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};

/**
 * React Query hook for free trials
 */
export const useFreeTrials = () => {
  return useQuery({
    queryKey: ["summary", "freeTrials"],
    queryFn: fetchFreeTrials,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};

/**
 * React Query hook for all users
 */
export const useAllUsers = (deviceLimitExceptionList = []) => {
  // Use only the length for stable query key
  const listKey = Array.isArray(deviceLimitExceptionList)
    ? deviceLimitExceptionList.length
    : 0;
  return useQuery({
    queryKey: ["summary", "allUsers", listKey],
    queryFn: () => fetchAllUsers(deviceLimitExceptionList),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 2, // Retry up to 2 times on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    enabled:
      deviceLimitExceptionList !== null &&
      deviceLimitExceptionList !== undefined,
  });
};

/**
 * React Query hook for staff students
 */
export const useStaffStudents = () => {
  return useQuery({
    queryKey: ["summary", "staffStudents"],
    queryFn: fetchStaffStudents,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};
